import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { TFunction } from 'react-i18next';
import { withInject, errorBoundary } from '@piwikpro/platform';
import {
  Layout,
  Stack,
  Divider,
  withTranslation,
  Card,
  Image,
  Text,
} from '@piwikpro/ui-components';
import { Notifications } from '@piwikpro/notification-crate';
import { ProductAnalytics } from '@piwikpro/product-analytics';
import { ContainerSettingsState } from '@piwikpro/container-settings-crate/redux';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';
import { OrganizationState } from '@piwikpro/organization-crate';
import { resolveConsentManagementTool } from '@piwikpro/apps-crate';
import {
  ManualInstall,
  CookiebotInstall,
  WordPressInstall,
  GoogleTagManagerInstall,
  ReactInstall,
  AngularInstall,
  ShopifyInstall,
  OtherMethodsInstall,
} from '@piwikpro/apps-crate/components/InstallationMethods';
import InstallManuallyIcon from '@piwikpro/apps-crate/components/InstallationMethods/icons/manually.svg';
import WordPressIcon from '@piwikpro/apps-crate/components/InstallationMethods/icons/wordpress.svg';
import GtmIcon from '@piwikpro/apps-crate/components/InstallationMethods/icons/gtm.svg';
import ReactIcon from '@piwikpro/apps-crate/components/InstallationMethods/icons/react.svg';
import AngularIcon from '@piwikpro/apps-crate/components/InstallationMethods/icons/angular.svg';
import ShopifyIcon from '@piwikpro/apps-crate/components/InstallationMethods/icons/shopify.svg';
import OtherMethodsIcon from '@piwikpro/apps-crate/components/InstallationMethods/icons/other.svg';
import { AdministrationState, STATE_ROOT } from '../../../../../redux';


const mapStateToProps = (state: AdministrationState & ContainerSettingsState & {
  session: {
    organizationName: SessionState['organizationName']
  }
  organization: OrganizationState
}) => {
  const { code, failed: installationCodeFailed } = state.containerSettings.app.installationCode;
  const { trackingDomain, trackingEndpointPath } = state.containerSettings.app.domainSettings;
  const {
    value: containerUrl,
    failed: containerUrlFailed,
  } = state.containerSettings.app.domainSettings.staticResourcesDomain;

  const app = state[STATE_ROOT].apps.details.settings.settings;

  return {
    license: state.organization.details.license?.current,
    organizationName: state.session.organizationName,
    snippet: code as NonNullable<typeof code>,
    containerUrl: `https://${containerUrl}`,
    app,
    cmp: app ? resolveConsentManagementTool(app.attributes) : null,
    installationCodeFailed,
    containerUrlFailed,
    trackingDomain,
    trackingEndpointPath,
    shopifyContainerSettingsAreLoading: trackingDomain.isLoading || trackingEndpointPath.isLoading,
    shopifyContainerSettingsFailed: trackingDomain.failed || trackingEndpointPath.failed,
  };
};

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  productAnalytics: ProductAnalytics
  notifications: Notifications
  Title: typeof React.Component
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
}

class Sites extends React.Component<Props> {
  constructor(props: Props) {
    super(props);

    this.handleCopyToClipboard = this.handleCopyToClipboard.bind(this);
  }

  handleCopyToClipboard = (result, productAnalyticsActionType) => {
    const { notifications, productAnalytics, t } = this.props;

    if (result) {
      notifications.push({
        text: t('components:notifications.copied.success'),
        type: 'success',
      });
      productAnalytics.push({ type: `${productAnalyticsActionType}/copy` });
    } else {
      notifications.push({
        text: t('components:notifications.copied.error'),
        type: 'error',
      });
    }
  };

  render() {
    const {
      Title,
      t,
      app,
      installationCodeFailed,
      productAnalytics,
      snippet,
      cmp,
      containerUrl,
      containerUrlFailed,
      shopifyContainerSettingsAreLoading,
      shopifyContainerSettingsFailed,
      trackingEndpointPath,
      trackingDomain,
      organizationName,
      license,
    } = this.props;

    if (!app) {
      return null;
    }

    return (
      <Title part={t('apps.details.tabs.sites.installation')}>
        <Layout>
          <Stack vertical>
            <Stack.Item fill>
              <Card>
                <Card.Accordion
                  clickableContent={(
                    <Stack verticalAlignment="center">
                      <Stack.Item>
                        <Image src={InstallManuallyIcon} displayType="block" />
                      </Stack.Item>
                      <Stack.Item fill wrap>
                        <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
                          {t('apps.details.installation.header')}
                        </Text>
                      </Stack.Item>
                    </Stack>
                  )}
                  clickableContentOnClick={(isOpened) => {
                    if (isOpened) {
                      productAnalytics.push({
                        type: '@platform/command/appInstallation/manualAsync',
                      });
                    }
                  }}
                >
                  <ManualInstall
                    app={app}
                    t={t}
                    installationCodeFailed={installationCodeFailed}
                    productAnalytics={productAnalytics}
                    snippet={snippet}
                    cmp={cmp || null}
                    handleCopyToClipboard={this.handleCopyToClipboard}
                  />
                </Card.Accordion>
              </Card>
            </Stack.Item>
            <Stack.Item fill>
              <Card>
                <Card.Accordion
                  clickableContent={(
                    <Stack verticalAlignment="center">
                      <Stack.Item>
                        <Image src={InstallManuallyIcon} displayType="block" />
                      </Stack.Item>
                      <Stack.Item fill wrap>
                        <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
                          {t('apps.details.installation.cookiebot.header')}
                        </Text>
                      </Stack.Item>
                    </Stack>
                  )}
                  clickableContentOnClick={(isOpened) => {
                    if (isOpened) {
                      productAnalytics.push({
                        type: '@platform/command/appInstallation/cookiebot',
                      });
                    }
                  }}
                >
                  <CookiebotInstall
                    app={app}
                    t={t}
                    installationCodeFailed={installationCodeFailed}
                    productAnalytics={productAnalytics}
                    snippet={snippet}
                    handleCopyToClipboard={this.handleCopyToClipboard}
                  />
                </Card.Accordion>
              </Card>
            </Stack.Item>
            <Stack.Item fill>
              <Card>
                <Card.Accordion
                  clickableContent={(
                    <Stack verticalAlignment="center">
                      <Stack.Item>
                        <Image src={WordPressIcon} displayType="block" />
                      </Stack.Item>
                      <Stack.Item fill wrap>
                        <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
                          {t('apps.details.installation.wordpress.title')}
                        </Text>
                      </Stack.Item>
                    </Stack>
                  )}
                  clickableContentOnClick={(isOpened) => {
                    if (isOpened) {
                      productAnalytics.push({ type: '@platform/command/appInstallation/wordpress' });
                    }
                  }}
                >
                  <WordPressInstall
                    app={app}
                    containerUrl={containerUrl}
                    containerUrlFailed={containerUrlFailed}
                    productAnalytics={productAnalytics}
                    t={t}
                    cmp={cmp || null}
                  />
                </Card.Accordion>
              </Card>
            </Stack.Item>
            <Stack.Item fill>
              <Card>
                <Card.Accordion
                  clickableContent={(
                    <Stack verticalAlignment="center">
                      <Stack.Item>
                        <Image src={GtmIcon} displayType="block" />
                      </Stack.Item>
                      <Stack.Item fill wrap>
                        <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
                          {t('apps.details.installation.gtm.title')}
                        </Text>
                      </Stack.Item>
                    </Stack>
                  )}
                  clickableContentOnClick={(isOpened) => {
                    if (isOpened) {
                      productAnalytics.push({
                        type: '@platform/command/appInstallation/googleTagManager',
                      });
                    }
                  }}
                >
                  <GoogleTagManagerInstall
                    app={app}
                    t={t}
                    installationCodeFailed={installationCodeFailed}
                    snippet={snippet}
                    cmp={cmp || null}
                    handleCopyToClipboard={this.handleCopyToClipboard}
                  />
                </Card.Accordion>
              </Card>
            </Stack.Item>
            <Stack.Item fill>
              <Card>
                <Card.Accordion
                  clickableContent={(
                    <Stack verticalAlignment="center">
                      <Stack.Item>
                        <Image src={ReactIcon} displayType="block" />
                      </Stack.Item>
                      <Stack.Item fill wrap>
                        <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
                          {t('apps.details.installation.react.title')}
                        </Text>
                      </Stack.Item>
                    </Stack>
                  )}
                  clickableContentOnClick={(isOpened) => {
                    if (isOpened) {
                      productAnalytics.push({
                        type: '@platform/command/appInstallation/react',
                      });
                    }
                  }}
                >
                  <ReactInstall
                    app={app}
                    containerUrlFailed={containerUrlFailed}
                    containerUrl={containerUrl}
                    t={t}
                    handleCopyToClipboard={this.handleCopyToClipboard}
                  />
                </Card.Accordion>
              </Card>
            </Stack.Item>
            <Stack.Item fill>
              <Card>
                <Card.Accordion
                  clickableContent={(
                    <Stack verticalAlignment="center">
                      <Stack.Item>
                        <Image src={AngularIcon} displayType="block" />
                      </Stack.Item>
                      <Stack.Item fill wrap>
                        <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
                          {t('apps.details.installation.angular.title')}
                        </Text>
                      </Stack.Item>
                    </Stack>
                  )}
                  clickableContentOnClick={(isOpened) => {
                    if (isOpened) {
                      productAnalytics.push({
                        type: '@platform/command/appInstallation/angular',
                      });
                    }
                  }}
                >
                  <AngularInstall
                    app={app}
                    containerUrl={containerUrl}
                    containerUrlFailed={containerUrlFailed}
                    t={t}
                    handleCopyToClipboard={this.handleCopyToClipboard}
                  />
                </Card.Accordion>
              </Card>
            </Stack.Item>
            <Stack.Item fill>
              <Card>
                <Card.Accordion
                  clickableContent={(
                    <Stack verticalAlignment="center">
                      <Stack.Item>
                        <Image src={ShopifyIcon} displayType="block" />
                      </Stack.Item>
                      <Stack.Item fill wrap>
                        <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
                          {t('apps.details.installation.shopify.title')}
                        </Text>
                      </Stack.Item>
                    </Stack>
                  )}
                  clickableContentOnClick={(isOpened) => {
                    if (isOpened) {
                      productAnalytics.push({
                        type: '@platform/command/appInstallation/shopify',
                      });
                    }
                  }}
                >
                  <ShopifyInstall
                    shopifyContainerSettingsAreLoading={shopifyContainerSettingsAreLoading}
                    shopifyContainerSettingsFailed={shopifyContainerSettingsFailed}
                    trackingEndpointPath={trackingEndpointPath}
                    trackingDomain={trackingDomain}
                    appId={app.id}
                    organizationName={organizationName}
                    license={license}
                    t={t}
                  />
                </Card.Accordion>
              </Card>
            </Stack.Item>
          </Stack>
          <Divider />
          <Stack vertical>
            <Stack.Item fill>
              <Card>
                <Card.Accordion clickableContent={(
                  <Stack verticalAlignment="center" horizontalAlignment="middle">
                    <Stack.Item>
                      <Image src={OtherMethodsIcon} displayType="block" />
                    </Stack.Item>
                    <Stack.Item fill wrap>
                      <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
                        {t('apps.details.installation.other-methods.title')}
                      </Text>
                    </Stack.Item>
                  </Stack>
                )}
                >
                  <OtherMethodsInstall
                    productAnalytics={productAnalytics}
                    t={t}
                  />
                </Card.Accordion>
              </Card>
            </Stack.Item>
          </Stack>
        </Layout>
      </Title>
    );
  }
}

export default connector(withInject<InjectedProps>({
  productAnalytics: 'productAnalytics',
  notifications: 'NotificationCrate.notifications',
  Title: 'TitleCrate.components.Title',
})(
  withTranslation('administration')(errorBoundary(Sites)),
));
